import {gql} from '@apollo/client';
import {css} from 'aphrodite';
import {DocumentNode} from 'graphql';
import React, {useMemo} from 'react';

import {assert} from 'Asserts';
import BenchlingConfig from 'BenchlingConfig';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import {StudyType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {QueryResultType, useBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import {getNewConditionCount, UpdateProcedureRunParams} from 'procedures/network/ProcedureRunDAO';
import getProcedureUIString, {ProceduresStringKeys, StringCase} from 'procedures/ProceduresStringKeys';
import {
  getFlowchartVersionIdForNavigation,
  getPowAgnosticProcedureFromFlowchartVersion,
  getPowAgnosticTemplateFlowchartVersionFromProcedureRun,
} from 'procedures/utils/powAgnosticFlowchartHelpers';
import useTimingEstimate from 'studies/progress/useTimingEstimate';
import {refreshProcedureRunDesign} from 'studies/sidepanel/TrayTaskUtils';
import {
  createDefaultConditionGroups,
  useProcedureRunUpdateParam,
} from 'studies/typed/shared/useExperimentDesignState';
import {
  getProcedureRunUIStringForStudyType,
  getProcedureTemplateStringForStudyType,
} from 'studies/TypedStudyUtils';
import getTestProps from 'test/lib/cypress/getTestProps';
import InlineBanner, {InlineBannerType} from 'ui-library/banners/InlineBanner';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';
import FlowchartSidebar from 'ui-library/reactflow/sidepanels/FlowchartSidebar';

import {StudyInaccessibleContentBanner} from '../utils/ui/StudyQueryBanners';
import {
  MultiProcedureRunStudyDesignTabContentsQuery,
  MultiProcedureRunStudyDesignTabContentsQueryVariables,
  MultiProcedureRunStudyDesignTabContentsQuery_procedureRun_ProcedureRunNode as ProcedureRunNode,
} from './__generated/MultiProcedureRunStudyDesignTabContentsQuery';
import {getActiveAndDownstreamMethodIds} from './activeMethodUtils';
import {buildConditionReplicateFlowchartInput} from './buildConditionReplicateFlowchartInput';
import {
  getConditionCreationUnknownErrorAction,
  onOpenAfterAddConditions,
  onSuccessAfterAddConditions,
} from './ConditionCreationTrayTask';
import {
  EditNumConditionGroupsButtons,
  runConditionGroupsButtonVariant,
} from './EditNumConditionGroupsButtons';
import getStudyDesignFromProcedureRun, {
  STUDY_DESIGN_FROM_PROCEDURE_RUN_FRAGMENT,
} from './getStudyDesignFromProcedureRun';
import {ProcedureRunTab} from './MultiProcedureRunOverviewTabContents';
import ProcedureRunDesignEditorLeftSidepanel from './ProcedureRunDesignEditorLeftSidepanel';
import ProcedureRunDesignEditorRightSidepanel from './ProcedureRunDesignEditorRightSidepanel';
import ProcedureRunDesignFlowchartEditor from './ProcedureRunDesignFlowchartEditor';
import {
  areProcedureRunInputsEvaluatedValuesValid,
  isNumConditionReplicatesValid,
  validateConditions,
} from './procedureRunStudyValidationUtils';
import {onAddConditions, onUpdateProcedureRun} from './procedureRunUpdateHandlers';
import StudyDesignTabSavingButtons, {StudyDesignTabEditState} from './StudyDesignTabSavingButtons';
import {
  ContextPanel,
  PARAMETER_VALUES_TAB_NAME,
  ParameterValuesTabContents,
  SETTINGS_TAB_NAME,
  SettingsTabContents,
  studyDesignTabStyles,
} from './StudyDesignTabSharedComponents';
import {combineEdgeSpecs, useProcedureRunDesignEdgeEdits} from './useProcedureRunDesignEdgeEdits';
import {useProcedureRunDesignFlowchartEdgeSpecs} from './useProcedureRunDesignFlowchartEdgeSpecs';
import {useProcedureRunMethodStatuses} from './useProcedureRunMethodStatuses';
import {useStudyDesignState} from './useStudyDesignState';

export const MULTI_PROCEDURE_RUN_STUDY_DESIGN_TAB_CONTENTS_QUERY: DocumentNode = gql`
  query MultiProcedureRunStudyDesignTabContentsQuery($procedureRunId: StrictString!)
  @skipAudit
  @engComponent(name: STUDIES) {
    procedureRun(id: $procedureRunId) {
      ...GetStudyDesignFromProcedureRunGraphQL
      ... on ProcedureRunNode {
        id
        procedureType
        defaultDataEntryMode
        flowchartIsExpanded
        templateProcedureFlowchartVersion {
          ...ExperimentalInputsFlowchartGraphQL
          ... on ProcedureFlowchartVersionNode {
            id
            largestTableTypeSizes {
              tableKind
              size
            }
            procedureFlowchart {
              id
              parent {
                id
              }
            }
          }
        }
        procedureFlowchartConfigVersionProxy {
          ... on ProcedureFlowchartConfigVersionProxyNode {
            id
            largestTableTypeSizes {
              tableKind
              size
            }
            procedure {
              id
            }
          }
        }
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('InaccessibleInfo').fragment}
  ${getFragmentWrapper('ProcedureFlowchartVersion').fragment}
  ${STUDY_DESIGN_FROM_PROCEDURE_RUN_FRAGMENT}
`;

function ProcedureRunDesignTabContents(props: {
  procedureRun: ProcedureRunNode;
  studyType: StudyType;
  studyId: string;
  setCurrentProcedureRunTab: (tab: ProcedureRunTab) => void;
}): JSX.Element {
  const {procedureRun} = props;
  const isFlowchartProcedureRunDesignEnabled = BenchlingConfig.STUDIES_ENABLE_FLOWCHART_PROCEDURE_RUN_DESIGN;
  const isSplitPoolEnabled = BenchlingConfig.STUDIES_ENABLE_SPLIT_POOL;

  const {
    experimentalInputs: initialExperimentalInputs,
    templateFlowchart,
    initialPersistedConditionGroups,
  } = useMemo(
    () =>
      getStudyDesignFromProcedureRun(procedureRun, {
        // Split-pool gates sparse per-method condition groups. Flowchart UI can render without
        // split-pool, but users still only create full bowling-lane conditions until it is on.
        requireBowlingLaneCoverage: !isSplitPoolEnabled,
      }),
    [procedureRun, isSplitPoolEnabled]
  );

  const flowchartVersion = getPowAgnosticTemplateFlowchartVersionFromProcedureRun(procedureRun);
  assert(!isInaccessible(flowchartVersion), 'Expected flowchart version to be accessible');
  const procedureId = getPowAgnosticProcedureFromFlowchartVersion(flowchartVersion).id;

  const {
    selectedMethodId,
    setSelectedMethodId,
    editState,
    setEditState,
    selectedDesignTabIndex,
    setSelectedDesignTabIndex,
    sidepanelName,
    setSidepanelName,
    conditions,
    setConditions,
    experimentalInputs,
    setExperimentalInputs,
    resetConditionsAndInputs,
    context,
    methodExecutionLimitState,
  } = useStudyDesignState({
    isPlan: false,
    initialExperimentalInputs,
    templateFlowchart,
    initialPersistedConditions: initialPersistedConditionGroups,
    createDefaultConditions: createDefaultConditionGroups,
    procedureType: procedureRun.procedureType,
    flowchartVersion,
    dataEntryMode: procedureRun.defaultDataEntryMode,
    procedureId,
    // Default to no method selected for flowchart procedure run design
    selectFirstMethodInitially: !isFlowchartProcedureRunDesignEnabled,
  });

  const isEmptyFlowchart = templateFlowchart.nodes.length === 0;

  // Each projection is gated by its own flag: the per-cell status map is used when flowchart
  // procedure run design is enabled, and the active method only when split-pool is also enabled.
  const {statusByNodeId, activeMethod, doesActiveMethodHaveDownstreamMethods} = useProcedureRunMethodStatuses(
    {
      procedureRun,
      methods: templateFlowchart.nodes,
      conditions,
      isFlowchartProcedureRunDesignEnabled,
      isSplitPoolEnabled,
    }
  );

  const edgeSpecs = useProcedureRunDesignFlowchartEdgeSpecs({
    isFlowchartProcedureRunDesignEnabled,
    conditions,
    methods: templateFlowchart.nodes,
    procedureRun,
    activeMethodId: activeMethod?.id ?? null,
    procedureEdges: templateFlowchart.edges,
  });
  const {drawnEdgeSpecs, addDrawnEdge, resetEdits} = useProcedureRunDesignEdgeEdits(conditions);
  // Union of derived and drawn edges, the source of truth for both rendering and the save payload.
  const allEdgeSpecs = useMemo(
    () => combineEdgeSpecs(edgeSpecs, drawnEdgeSpecs),
    [edgeSpecs, drawnEdgeSpecs]
  );
  const conditionReplicateFlowchart = useMemo(
    () =>
      isSplitPoolEnabled
        ? buildConditionReplicateFlowchartInput({conditions, edgeSpecs: allEdgeSpecs})
        : undefined,
    [isSplitPoolEnabled, conditions, allEdgeSpecs]
  );

  const newConditionCoveredMethodIds = useMemo(() => {
    if (!isSplitPoolEnabled || activeMethod == null) {
      return undefined;
    }
    return [...getActiveAndDownstreamMethodIds(templateFlowchart.nodes, activeMethod.id)];
  }, [isSplitPoolEnabled, activeMethod, templateFlowchart.nodes]);

  const isEditDisabledForSplitPool = isSplitPoolEnabled && activeMethod == null;
  const lowerMethodString = getProcedureUIString(ProceduresStringKeys.METHOD, procedureRun.procedureType, {
    stringCase: StringCase.LOWER,
  });
  const editDisabledTooltip = isEditDisabledForSplitPool
    ? `You can only edit the run design after every replicate in the previous ${lowerMethodString} is complete and before the next one starts`
    : undefined;

  const procedureRunUpdateParam = useProcedureRunUpdateParam(
    procedureRun.id,
    conditions,
    experimentalInputs,
    context?.evaluatedValues,
    conditionReplicateFlowchart
  );

  const editNumColsButtons = (
    <EditNumConditionGroupsButtons
      conditions={conditions}
      setConditions={setConditions}
      methodExecutionLimitState={methodExecutionLimitState}
      procedureType={procedureRun.procedureType}
      showReplicateLimitLabel={true}
      {...runConditionGroupsButtonVariant({
        conditions,
        methodExecutionLimitState,
        procedureType: procedureRun.procedureType,
      })}
    />
  );
  const estimateResult = useTimingEstimate<{updates: Array<UpdateProcedureRunParams>}>(
    '/1/api/procedure-runs:estimate',
    // Don't want an estimate if there are no new conditions
    getNewConditionCount(procedureRunUpdateParam) > 0 ? {updates: [procedureRunUpdateParam]} : null
  );

  async function handleSave(): Promise<void> {
    setEditState(StudyDesignTabEditState.SAVING);
    assert(conditions != null, 'Conditions should not be null for existing run');
    assert(experimentalInputs != null, 'Experimental inputs should not be null for existing run.');
    assert(context != null, 'Expected context to be non-null');

    const newConditionCount = getNewConditionCount(procedureRunUpdateParam);
    const hasNewConditions = newConditionCount > 0;
    const saveProcedureRun = isSplitPoolEnabled ? onUpdateProcedureRun : onAddConditions;
    await saveProcedureRun(
      props.studyId,
      procedureRun.procedureType,
      props.studyType,
      procedureRunUpdateParam,
      {
        // Runs on success for both sync and async saves. Reset edit state here rather than via a
        // remount. Refreshing the design query caches the new edges as derived for a later remount.
        // Only the condition flow needs the details view.
        onSuccess: async () => {
          setEditState(StudyDesignTabEditState.SAVED);
          await refreshProcedureRunDesign(procedureRun.id);
          if (hasNewConditions) {
            await onSuccessAfterAddConditions(procedureRun.id);
          }
        },
        // Adding a condition opens the Overview tab to show the newly added conditions. Edges aren't
        // shown in the overview tab, so no navigation is needed.
        onOpen: () => {
          if (hasNewConditions) {
            onOpenAfterAddConditions(props.studyId, procedureRun.id);
            props.setCurrentProcedureRunTab(ProcedureRunTab.OVERVIEW);
          }
        },
        onUnknownError: getConditionCreationUnknownErrorAction(
          props.studyId,
          props.studyType,
          procedureRun.id,
          procedureRun.procedureType,
          newConditionCount,
          // Go back to editing state, so the user can try to re-submit
          () => {
            setEditState(StudyDesignTabEditState.EDITING);
          }
        ),
      }
    );
  }

  // TODO (BNCH-156220): Remove this header in flowchart UI and move buttons elsewhere
  const designHeader = !procedureRun.flowchartIsExpanded && (
    <div className={css(studyDesignTabStyles.header)}>
      <div className={css(studyDesignTabStyles.tabTitle)}>Design</div>
      <div className={css(studyDesignTabStyles.bannerAndButtons)}>
        {editState === StudyDesignTabEditState.EDITING && (
          <InlineBanner
            bannerType={InlineBannerType.INFO}
            text="New worksheets will be created for new conditions."
          />
        )}
        <StudyDesignTabSavingButtons
          onCancel={() => {
            setEditState(StudyDesignTabEditState.SAVED);
            resetConditionsAndInputs(initialPersistedConditionGroups, initialExperimentalInputs);
            resetEdits();
          }}
          onEdit={() => {
            setEditState(StudyDesignTabEditState.EDITING);
          }}
          onSave={handleSave}
          editState={editState}
          isSaveDisabled={
            !areProcedureRunInputsEvaluatedValuesValid(context.evaluatedValues) ||
            !validateConditions(conditions) ||
            !isNumConditionReplicatesValid(methodExecutionLimitState) ||
            (conditions.length === initialPersistedConditionGroups.length && drawnEdgeSpecs.length === 0)
          }
          isEditDisabled={isEditDisabledForSplitPool}
          editDisabledTooltip={editDisabledTooltip}
        />
      </div>
    </div>
  );

  if (isEmptyFlowchart) {
    return (
      <div className={css(studyDesignTabStyles.bufferTabWrapper)}>
        <InlineBanner
          bannerType={InlineBannerType.INFO}
          text={`No ${getProcedureUIString(ProceduresStringKeys.METHOD, procedureRun.procedureType, {
            stringCase: StringCase.LOWER,
            plural: true,
          })} have been configured for this ${getProcedureUIString(
            ProceduresStringKeys.PROCEDURE,
            procedureRun.procedureType,
            {stringCase: StringCase.LOWER}
          )}.`}
        />
      </div>
    );
  }

  // If the flowchart procedure run design is not enabled, we should have a selected method id
  if (!isFlowchartProcedureRunDesignEnabled) {
    assert(
      selectedMethodId != null,
      'Expected selected method id to be non-null for non-flowchart (legacy) procedure run design'
    );
  }

  return (
    <div
      className={css(studyDesignTabStyles.designBackground)}
      {...getTestProps({component: 'ProcedureRunDesignTabContents', element: 'root'})}
    >
      {designHeader}
      {isFlowchartProcedureRunDesignEnabled ? (
        <ProcedureRunDesignFlowchartEditor
          conditions={conditions}
          methods={templateFlowchart.nodes}
          activeMethodId={activeMethod?.id ?? null}
          procedureType={procedureRun.procedureType}
          selectedMethodId={selectedMethodId}
          onChangeSelectedMethod={setSelectedMethodId}
          isInEditState={editState === StudyDesignTabEditState.EDITING}
          allowEditingPersistedConditions={false}
          statusByNodeId={statusByNodeId}
          edgeSpecs={allEdgeSpecs}
          onDrawEdge={addDrawnEdge}
          leftPanelChildren={
            <ProcedureRunDesignEditorLeftSidepanel
              isSplitPoolEnabled={isSplitPoolEnabled}
              activeMethodName={activeMethod?.name ?? null}
              doesActiveMethodHaveDownstreamMethods={doesActiveMethodHaveDownstreamMethods}
              conditions={conditions}
              setConditions={setConditions}
              experimentalInputs={experimentalInputs}
              setExperimentalInputs={setExperimentalInputs}
              editState={editState}
              procedureType={procedureRun.procedureType}
              methodExecutionLimitState={methodExecutionLimitState}
              studyType={props.studyType}
              dataEntryMode={procedureRun.defaultDataEntryMode}
              isPlan={false}
              procedureId={procedureId}
              flowchartVersionId={getFlowchartVersionIdForNavigation(flowchartVersion)}
              newConditionCoveredMethodIds={newConditionCoveredMethodIds}
            />
          }
          // Render the Design panel contents only once a method is selected: the sidepanel requires a
          // non-null selectedMethodId, and the editor keeps the right panel closed without a selection.
          rightPanelChildren={
            selectedMethodId != null && (
              <ProcedureRunDesignEditorRightSidepanel
                templateFlowchart={templateFlowchart}
                selectedMethodId={selectedMethodId}
                procedureType={procedureRun.procedureType}
                context={context}
              />
            )
          }
        />
      ) : (
        <div className={css(studyDesignTabStyles.leftSidepanelContainer)}>
          <FlowchartSidebar
            icons={[
              {
                name: 'note-table',
                onClick: () => {
                  setSidepanelName(PARAMETER_VALUES_TAB_NAME);
                },
              },
              {
                name: 'settings',
                onClick: () => {
                  setSidepanelName(SETTINGS_TAB_NAME);
                },
              },
            ]}
            selectedIconIndex={selectedDesignTabIndex}
            setSelectedIconIndex={setSelectedDesignTabIndex}
          />
          <ContextPanel
            sidepanelName={sidepanelName}
            sidepanelHeaderContent={
              selectedDesignTabIndex === 0 && editState === StudyDesignTabEditState.EDITING
                ? editNumColsButtons
                : undefined
            }
          >
            {selectedDesignTabIndex === 0 ? (
              <ParameterValuesTabContents
                methods={templateFlowchart.nodes}
                procedureFlowchart={templateFlowchart}
                selectedMethodId={selectedMethodId!} // We assert this is non-null above
                onChangeSelectedMethod={setSelectedMethodId}
                procedureType={procedureRun.procedureType}
                context={context}
              />
            ) : (
              <SettingsTabContents
                conditions={conditions}
                setConditions={setConditions}
                experimentalInputs={experimentalInputs}
                setExperimentalInputs={setExperimentalInputs}
                locked={editState !== StudyDesignTabEditState.EDITING}
                procedureType={procedureRun.procedureType}
                methodExecutionLimitState={methodExecutionLimitState}
                studyType={props.studyType}
                dataEntryMode={procedureRun.defaultDataEntryMode}
                estimateResult={estimateResult}
                isPlan={false}
              />
            )}
          </ContextPanel>
        </div>
      )}
    </div>
  );
}

export default function QueryingProcedureRunDesignTabContents({
  procedureRunId,
  studyType,
  studyId,
  setCurrentProcedureRunTab,
}: {
  procedureRunId: string;
  studyType: StudyType;
  studyId: string;
  setCurrentProcedureRunTab: (tab: ProcedureRunTab) => void;
}): JSX.Element {
  const procedureRunUIString = getProcedureRunUIStringForStudyType(studyType);
  const procedureTemplateString = getProcedureTemplateStringForStudyType(studyType);
  const result = useBenchlingQuery<
    MultiProcedureRunStudyDesignTabContentsQuery,
    MultiProcedureRunStudyDesignTabContentsQueryVariables
  >(MULTI_PROCEDURE_RUN_STUDY_DESIGN_TAB_CONTENTS_QUERY, {variables: {procedureRunId}});

  if (result.type !== QueryResultType.SUCCESS) {
    return <CenteredHorizontalThrobber />;
  }

  const {data} = result;
  if (isInaccessible(data.procedureRun)) {
    return (
      <StudyInaccessibleContentBanner
        inaccessibleInfo={data.procedureRun}
        modelType={ModelTypeConstants.PROCEDURE_RUN}
        displayErrorText={`This ${procedureRunUIString} is no longer accessible. The ${procedureRunUIString} may have been moved into a project that you do not have access to.`}
      />
    );
  }
  const flowchartVersion = getPowAgnosticTemplateFlowchartVersionFromProcedureRun(data.procedureRun);
  if (isInaccessible(flowchartVersion)) {
    return (
      <StudyInaccessibleContentBanner
        inaccessibleInfo={flowchartVersion}
        modelType={ModelTypeConstants.PROCEDURE}
        displayErrorText={`The design of this ${procedureRunUIString} is not accessible. The ${procedureTemplateString} associated with this ${procedureRunUIString} may have been moved into a template collection that you do not have access to.`}
      />
    );
  }

  return (
    <ProcedureRunDesignTabContents
      procedureRun={data.procedureRun}
      studyType={studyType}
      studyId={studyId}
      setCurrentProcedureRunTab={setCurrentProcedureRunTab}
    />
  );
}
